/**
 * Form model of the app registration wizard (#1712) and its mapping to the
 * POST /api/apps body.
 *
 * The id patterns are a copy of web-portal/api/shared/appRecipeService.ts
 * (APP_ID_PATTERN / VERSION_PATTERN) for live feedback only - the API checks
 * again and is authoritative (it answers 400 with details.errors).
 */

import type { AppRecipeInput } from '../services/appsService';
import type { DetectionRule } from '../services/types';

export const APP_ID_PATTERN = /^[a-z0-9][a-z0-9.-]{0,62}$/;
export const VERSION_PATTERN = /^[A-Za-z0-9._-]{1,32}$/;

export type Provider = AppRecipeInput['provider'];
export type DetectionType = DetectionRule['type'];

export interface DetectionDraft {
  type: DetectionType;
  /** path (file), key (registry), product code (msiProductCode) or script body. */
  value: string;
}

export interface RecipeDraft {
  appId: string;
  version: string;
  name: string;
  vendor: string;
  provider: Provider;
  installerId: string;
  command: string;
  args: string;
  script: string;
  timeoutMin: string;
  detection: DetectionDraft[];
  prereqs: string[];
}

export const emptyDraft = (): RecipeDraft => ({
  appId: '',
  version: '',
  name: '',
  vendor: '',
  provider: 'choco',
  installerId: '',
  command: '',
  args: '',
  script: '',
  timeoutMin: '30',
  detection: [{ type: 'file', value: '' }],
  prereqs: [],
});

export type DraftErrors = Partial<Record<'appId' | 'version' | 'name' | 'vendor' | 'timeoutMin' | 'detection' | 'install', string>>;

export function validateDraft(d: RecipeDraft): DraftErrors {
  const e: DraftErrors = {};
  if (!APP_ID_PATTERN.test(d.appId)) {
    e.appId = 'Nur Kleinbuchstaben, Ziffern, Punkt und Bindestrich; beginnt mit Buchstabe oder Ziffer (max. 63)';
  }
  if (!VERSION_PATTERN.test(d.version)) e.version = 'Buchstaben, Ziffern, Punkt, Unter- und Bindestrich (max. 32)';
  if (!d.name.trim()) e.name = 'Pflichtfeld';
  if (!d.vendor.trim()) e.vendor = 'Pflichtfeld';
  const t = Number(d.timeoutMin);
  if (!Number.isInteger(t) || t <= 0) e.timeoutMin = 'Ganze Minuten > 0';
  if (d.detection.length === 0 || d.detection.some((r) => !r.value.trim())) e.detection = 'Jede Erkennungsregel braucht einen Wert';
  if (d.provider === 'script' && !d.script.trim()) e.install = 'Script ist Pflicht für die Installationsart Script';
  if ((d.provider === 'msi' || d.provider === 'exe') && !d.installerId.trim() && !d.command.trim()) {
    e.install = 'Installer-ID oder Befehl ist Pflicht für MSI/EXE';
  }
  return e;
}

function toRule(r: DetectionDraft): DetectionRule {
  const value = r.value.trim();
  switch (r.type) {
    case 'registry':
      return { type: 'registry', key: value, operator: 'exists' };
    case 'msiProductCode':
      return { type: 'msiProductCode', productCode: value, operator: 'exists' };
    case 'script':
      return { type: 'script', script: value };
    default:
      return { type: 'file', path: value, operator: 'exists' };
  }
}

const opt = (v: string): string | undefined => (v.trim() ? v.trim() : undefined);

/** The POST body. osTypes is ws2022 only: the lab base is WS2022 (Win11 after D14). */
export function toRecipeInput(d: RecipeDraft): AppRecipeInput {
  return {
    appId: d.appId,
    version: d.version,
    name: d.name.trim(),
    vendor: d.vendor.trim(),
    platform: 'windows',
    provider: d.provider,
    osTypes: ['ws2022'],
    ...(opt(d.installerId) ? { installerId: opt(d.installerId) } : {}),
    install: {
      ...(opt(d.command) ? { command: opt(d.command) } : {}),
      ...(opt(d.args) ? { args: opt(d.args) } : {}),
      ...(d.provider === 'script' && opt(d.script) ? { script: d.script } : {}),
      timeoutMin: Number(d.timeoutMin),
      successExitCodes: [0, 3010],
      reboot: 'none',
      logPaths: [],
    },
    prereqs: d.prereqs,
    detection: d.detection.map(toRule),
    delivery: 'baked',
  };
}
